import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  BellAlertIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  CircleStackIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  LifebuoyIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { agentsAPI, analyticsAPI, chatAPI, signupHealthAPI, supportAPI } from '../services/api';
import ragService from '../services/rag.service';
import { getNotificationContacts, getTenants } from '../services/tenant.service';
import { displayLabel, formatDate, formatDateTime, plural } from '../utils/format';
import { useAuthStore } from '../store/authStore';
import { useLicense } from '../hooks/useLicense';
import { openTenantAdminWorkspace } from '../utils/tenantAdminWorkspace';

const cardBase = 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm';
const subtleText = 'text-sm text-gray-500 dark:text-gray-400';

const fmtDate = (value) => formatDateTime(value, '-');

const getLeadName = (lead) => lead?.name || lead?.email || lead?.phone || 'Unknown lead';
const getTicketName = (ticket) => ticket?.subject || ticket?.body || 'Support ticket';
const isHotLead = (lead) => String(lead?.priority_tier || '').toLowerCase() === 'hot' || Number(lead?.priority_score || 0) >= 70;

const Tile = ({ label, value, hint, icon: Icon, tone = 'text-indigo-600', to }) => {
  const body = (
    <div className={`${cardBase} h-full ${to ? 'hover:border-indigo-200 dark:hover:border-indigo-700 transition-colors' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={subtleText}>{label}</p>
          <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">{value}</p>
          {hint && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
        </div>
        {Icon && <Icon className={`w-6 h-6 flex-shrink-0 ${tone}`} />}
      </div>
    </div>
  );

  return to ? <Link to={to}>{body}</Link> : body;
};

const Section = ({ title, children, action }) => (
  <section className={cardBase}>
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const ListRow = ({ title, subtitle, meta, to, onClick, tone = 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' }) => {
  const content = (
    <div className="flex items-start justify-between gap-3 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{title}</p>
        {subtitle && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{subtitle}</p>}
      </div>
      {meta && (
        <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
          {meta}
        </span>
      )}
    </div>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="block w-full text-left hover:bg-gray-50 dark:hover:bg-gray-900/30 -mx-2 px-2 rounded-md"
      >
        {content}
      </button>
    );
  }
  return to ? <Link to={to} className="block hover:bg-gray-50 dark:hover:bg-gray-900/30 -mx-2 px-2 rounded-md">{content}</Link> : content;
};

const EmptyState = ({ children }) => (
  <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">{children}</div>
);

const WorkspaceDashboardPage = () => {
  const { tenantId } = useParams();
  const location = useLocation();
  const { user } = useAuthStore();
  const { license, tier, isGrowthOrScale } = useLicense();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({
    stats: null,
    leads: [],
    hotLeads: [],
    assignedLeads: [],
    supportMetrics: null,
    tickets: [],
    assignedTickets: [],
    conversations: [],
    conversationTotal: 0,
    collections: [],
    ragStats: null,
    tenants: [],
  });

  const isSuperadmin = user?.role === 'superadmin' || user?.is_superuser;
  const isTenantAdmin = user?.tenant_role === 'admin' || user?.tenant_role === 'owner';
  const isTenantUser = !isSuperadmin && !isTenantAdmin;
  const isPlatformMode = location.pathname.startsWith('/platform');
  const canOpenConversationHistory = isSuperadmin || isTenantAdmin;
  const conversationEntitlement = license?.feature_entitlements?.conversation_history;
  const canLoadConversationHistory = isSuperadmin || (
    isTenantAdmin && (
      typeof conversationEntitlement === 'boolean'
        ? conversationEntitlement
        : isGrowthOrScale
    )
  );
  const params = tenantId ? { tenant_id: tenantId } : {};
  const basePath = isPlatformMode ? '/platform' : tenantId ? `/t/${tenantId}` : '';
  const path = (p) => `${basePath}${p}`;

  const load = async () => {
    setRefreshing(true);
    setError('');
    try {
      const calls = [
        analyticsAPI.getStats(30, params),
        analyticsAPI.getLeads({ ...params, days: 30, limit: 6, sort_by: 'created_at' }),
        analyticsAPI.getLeads({ ...params, follow_up: 'uncontacted', priority_tier: 'hot', limit: 6, sort_by: 'priority' }),
        supportAPI.getMetrics(params),
        supportAPI.getTickets({ ...params, status: 'open', limit: 6 }),
        ragService.getCollections(),
        ragService.getStats(),
      ];

      if (canLoadConversationHistory) {
        calls.push(chatAPI.listAllConversations({ ...params, skip: 0, limit: 6 }));
      } else {
        calls.push(Promise.resolve({ conversations: [], total: 0 }));
      }

      const currentUserId = user?.id || user?.user_id;
      if (isTenantUser && currentUserId) {
        calls.push(analyticsAPI.getLeads({ ...params, assigned_to: currentUserId, limit: 6, sort_by: 'created_at' }));
        calls.push(supportAPI.getTickets({ ...params, assigned_to: currentUserId, limit: 6 }));
      } else {
        calls.push(Promise.resolve({ data: { leads: [] } }));
        calls.push(Promise.resolve({ data: { tickets: [] } }));
      }

      if (isSuperadmin && isPlatformMode) {
        calls.push(getTenants(null, { limit: 200 }));
      } else {
        calls.push(Promise.resolve([]));
      }

      // Setup facts for the admin view, read rather than guessed: where the
      // widget has actually loaded, and whether anyone receives lead alerts.
      const adminTenantId = tenantId || user?.tenant_id;
      if (!isPlatformMode && !isTenantUser && adminTenantId) {
        calls.push(agentsAPI.getInstallStatus());
        calls.push(getNotificationContacts(adminTenantId));
      } else {
        calls.push(Promise.resolve({ data: null }));
        calls.push(Promise.resolve(null));
      }

      // Superadmins see failed signups on the platform dashboard, not only on
      // their own page: five prospects were lost before anyone looked.
      calls.push(isSuperadmin && isPlatformMode
        ? signupHealthAPI.get({ days: 7 })
        : Promise.resolve({ data: null }));

      const [
        statsRes,
        leadsRes,
        hotLeadsRes,
        supportMetricsRes,
        ticketsRes,
        collectionsRes,
        ragStatsRes,
        conversationsRes,
        assignedLeadsRes,
        assignedTicketsRes,
        tenantsRes,
        installRes,
        alertContactsRes,
        signupHealthRes,
      ] = await Promise.allSettled(calls);

      const next = {
        stats: statsRes.status === 'fulfilled' ? (statsRes.value.data?.stats ?? null) : null,
        leads: leadsRes.status === 'fulfilled' ? (leadsRes.value.data?.leads || []) : [],
        hotLeads: hotLeadsRes.status === 'fulfilled' ? (hotLeadsRes.value.data?.leads || []) : [],
        supportMetrics: supportMetricsRes.status === 'fulfilled' ? supportMetricsRes.value.data : null,
        tickets: ticketsRes.status === 'fulfilled' ? (ticketsRes.value.data?.tickets || []) : [],
        collections: collectionsRes.status === 'fulfilled' ? (collectionsRes.value.collections || []) : [],
        ragStats: ragStatsRes.status === 'fulfilled' ? ragStatsRes.value : null,
        conversations: conversationsRes.status === 'fulfilled' ? (conversationsRes.value.conversations || []) : [],
        conversationTotal: conversationsRes.status === 'fulfilled' ? (conversationsRes.value.total || 0) : 0,
        assignedLeads: assignedLeadsRes.status === 'fulfilled' ? (assignedLeadsRes.value.data?.leads || []) : [],
        assignedTickets: assignedTicketsRes.status === 'fulfilled' ? (assignedTicketsRes.value.data?.tickets || []) : [],
        tenants: tenantsRes.status === 'fulfilled' ? (tenantsRes.value || []) : [],
        install: installRes.status === 'fulfilled' ? installRes.value?.data : null,
        alertContacts: alertContactsRes.status === 'fulfilled' ? alertContactsRes.value : null,
        signupHealth: signupHealthRes.status === 'fulfilled' ? signupHealthRes.value?.data : null,
      };

      setData(next);

      const failed = [
        statsRes,
        leadsRes,
        supportMetricsRes,
        ticketsRes,
        collectionsRes,
        ragStatsRes,
      ].some((r) => r.status === 'rejected');
      if (failed) {
        setError('Some dashboard sections could not load. The rest of the workspace is still available.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, user?.id, user?.tenant_role, location.pathname, isSuperadmin, isGrowthOrScale, conversationEntitlement]);

  const stats = data.stats || {};
  const performance = stats.performance_summary || {};
  const followUp = stats.follow_up || {};
  const byStatus = stats.by_status || {};
  // Open means not yet resolved: waiting or in progress, as the SLA counts.
  const openTickets = data.supportMetrics?.unresolved ?? data.supportMetrics?.open ?? 0;
  const hotUncontacted = data.hotLeads.filter((lead) => String(lead.status || '').toLowerCase() === 'new');
  const uncontacted = followUp.uncontacted ?? byStatus.new ?? 0;
  const missed2h = followUp.missed_2h || 0;
  const missed24h = followUp.missed_24h || 0;
  const leadConversionRate = stats.conversion_rate != null ? `${stats.conversion_rate}%` : '-';
  const supportDeflection = data.supportMetrics?.deflection_rate != null
    ? `${Math.round(data.supportMetrics.deflection_rate * 100)}%`
    : '-';
  const knowledgePages = data.ragStats?.total_documents || data.collections.reduce((sum, c) => sum + (c.documents_count || 0), 0);
  const agentHealth = useMemo(() => {
    const issues = [];
    if (data.collections.length === 0) issues.push('KB missing');
    if (!knowledgePages) issues.push('No sources');
    if (tier === 'starter') issues.push('WhatsApp handoff may be locked');
    if (issues.length === 0) return { label: 'Live', tone: 'text-green-700 bg-green-50 border-green-200' };
    return { label: issues[0], tone: 'text-amber-700 bg-amber-50 border-amber-200' };
  }, [data.collections.length, knowledgePages, tier]);

  // A failed load must not read as zero: "0 leads" and "could not load" are
  // different answers, and the Leads page would show the real number.
  const periodLeads = data.stats
    ? (performance.period_leads ?? stats.leads_in_period ?? 0)
    : '—';
  const installHosts = (data.install?.agents || []).flatMap((a) => a.hosts || [])
    .sort((a, b) => String(b.last_seen).localeCompare(String(a.last_seen)));
  const installKnown = data.install != null;
  const widgetInstalled = installHosts.length > 0;
  const alertEmails = data.alertContacts?.notification_emails || [];
  const alertPhones = data.alertContacts?.notification_phones || [];
  const alertsKnown = data.alertContacts != null;
  // Email alerts fall back to the workspace admins when no address is set, so
  // only a missing WhatsApp number on a plan that includes WhatsApp alerts
  // means new leads can go unseen on the phone.
  const whatsappGap = alertsKnown && isGrowthOrScale && alertPhones.length === 0;

  const platformTenants = data.tenants || [];
  const trialTenants = platformTenants.filter((t) => String(t.subscription_status || '').toLowerCase().includes('trial'));
  const activeTenants = platformTenants.filter((t) => t.is_active !== false);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (isSuperadmin && isPlatformMode) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <Header
          title="Dashboard"
          subtitle="Platform operating view: tenants, trials, health and noisy work."
          refreshing={refreshing}
          onRefresh={load}
        />
        {error && <Alert>{error}</Alert>}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Tile label="Active tenants" value={activeTenants.length} hint={`${platformTenants.length} total tenants loaded`} icon={UserGroupIcon} to="/platform/tenants" />
          <Tile label="Trial tenants" value={trialTenants.length} hint="Watch expiry and promotion" icon={ClockIcon} tone="text-amber-600" to="/platform/tenants" />
          <Tile label="Open support tickets" value={openTickets} hint="Across visible scope" icon={LifebuoyIcon} tone="text-amber-600" to="/platform/support" />
          <Tile label="System health" value="Check" hint="Smoke, API and app status" icon={CheckCircleIcon} tone="text-green-600" to="/platform/health-check" />
        </div>
        {data.signupHealth && (() => {
          const didNotFinish = data.signupHealth.funnel?.not_completed || 0;
          const noAccount = (data.signupHealth.signin_without_account || []).length;
          const failed = didNotFinish + noAccount;
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Tile
                label="Failed signups (7 days)"
                value={failed}
                hint={`${didNotFinish} didn't finish · ${noAccount} signed in with no account`}
                icon={UserGroupIcon}
                tone={failed ? 'text-red-600' : 'text-green-600'}
                to="/platform/signup-health"
              />
              <Tile
                label="Finished signing up (7 days)"
                value={data.signupHealth.funnel?.completed || 0}
                hint={`of ${data.signupHealth.funnel?.people_sent_a_code || 0} sent a code`}
                icon={CheckCircleIcon}
                tone="text-green-600"
                to="/platform/signup-health"
              />
            </div>
          );
        })()}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Section title="Recent tenants" action={<Link className="text-sm text-indigo-600" to="/platform/tenants">View tenants</Link>}>
            {platformTenants.length ? platformTenants.slice(0, 6).map((tenant) => (
              <ListRow
                key={tenant.id || tenant.slug}
                title={tenant.name || tenant.slug || 'Tenant'}
                subtitle={tenant.domain || tenant.billing_email || tenant.id}
                meta={tenant.subscription_tier || 'tier'}
                onClick={() => openTenantAdminWorkspace(tenant.id)}
              />
            )) : <EmptyState>No tenants loaded.</EmptyState>}
          </Section>
          <Section title="Platform attention">
            <ListRow title="Failed deploy or smoke checks" subtitle="Use Health Check and GitHub Actions until CI health is surfaced here." meta="manual" tone="bg-amber-100 text-amber-700" to="/platform/health-check" />
            <ListRow title="DB and storage usage" subtitle="Use Azure and Plan & Usage until platform cost metrics are wired." meta="manual" tone="bg-blue-100 text-blue-700" to="/platform/plan-usage" />
            <ListRow title="Notification failures" subtitle="Check lead and support notification pages per tenant." meta="manual" tone="bg-violet-100 text-violet-700" to="/platform/notification-settings" />
          </Section>
        </div>
      </div>
    );
  }

  if (isTenantUser) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <Header
          title="Dashboard"
          subtitle="Your assigned leads, support tickets and follow-ups in one place."
          refreshing={refreshing}
          onRefresh={load}
        />
        {error && <Alert>{error}</Alert>}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Tile label="My assigned leads" value={data.assignedLeads.length} hint="Recently assigned to you" icon={UserGroupIcon} to={path('/analytics')} />
          <Tile label="My support tickets" value={data.assignedTickets.length} hint="Open or recent tickets" icon={LifebuoyIcon} tone="text-amber-600" to={path('/support')} />
          <Tile label="Follow-ups due" value={uncontacted} hint={`${missed2h} older than 2h`} icon={ClockIcon} tone="text-red-600" to={path('/analytics?follow_up=uncontacted')} />
          <Tile label="Recent leads" value={data.leads.length} hint="Last 30 days loaded" icon={SparklesIcon} tone="text-blue-600" to={path('/analytics')} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Section title="My assigned leads" action={<Link className="text-sm text-indigo-600" to={path('/analytics')}>View leads</Link>}>
            {data.assignedLeads.length ? data.assignedLeads.map((lead) => (
              <ListRow
                key={lead.id}
                title={getLeadName(lead)}
                subtitle={lead.user_query || lead.chat_summary || lead.notes}
                meta={displayLabel(lead.priority_tier || lead.status || 'lead')}
                tone={isHotLead(lead) ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}
                to={path('/analytics')}
              />
            )) : <EmptyState>No assigned leads right now.</EmptyState>}
          </Section>
          <Section title="My support tickets" action={<Link className="text-sm text-indigo-600" to={path('/support')}>Open support</Link>}>
            {data.assignedTickets.length ? data.assignedTickets.map((ticket) => (
              <ListRow
                key={ticket.id}
                title={getTicketName(ticket)}
                subtitle={ticket.name || ticket.email || ticket.phone}
                meta={displayLabel(ticket.priority || ticket.status || 'ticket')}
                tone="bg-amber-100 text-amber-700"
                to={path('/support')}
              />
            )) : <EmptyState>No assigned tickets right now.</EmptyState>}
          </Section>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <Header
        title="Dashboard"
        subtitle="What needs attention across leads, conversations, support and setup."
        refreshing={refreshing}
        onRefresh={load}
      />
      {error && <Alert>{error}</Alert>}

      {installKnown && !widgetInstalled && (
        <div className="rounded-xl border-2 border-indigo-300 bg-indigo-50 p-5 dark:border-indigo-700 dark:bg-indigo-900/20">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Put your agent on your website</h2>
          <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
            Visitors can only ask questions and leave their details once the chat widget is on your site.
            It is one line of code, with steps for WordPress, Shopify, Wix, Squarespace and Google Tag Manager,
            or an email you can forward to your developer.
          </p>
          <Link
            to={path('/agent-management?install=first')}
            className="mt-3 inline-flex min-h-[44px] items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700"
          >
            Install on your website
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Tile label="Leads captured" value={periodLeads} hint={`Last ${stats.period_days || 30} days`} icon={UserGroupIcon} to={path('/analytics')} />
        <Tile label="Open support tickets" value={openTickets} hint="Awaiting team action" icon={LifebuoyIcon} tone="text-amber-600" to={path('/support')} />
        <Tile label="Not contacted yet" value={uncontacted} hint={`${missed2h} older than 2h, ${missed24h} older than 24h`} icon={ClockIcon} tone="text-red-600" to={path('/analytics?follow_up=uncontacted')} />
        <Tile label="Agent health" value={agentHealth.label} hint={`${knowledgePages || 0} knowledge sources`} icon={CheckCircleIcon} tone={agentHealth.label === 'Live' ? 'text-green-600' : 'text-amber-600'} to={path('/agent-management')} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Section title="Attention queue" action={<Link className="text-sm text-indigo-600" to={path('/analytics')}>View leads</Link>}>
          {hotUncontacted.length > 0 ? hotUncontacted.slice(0, 4).map((lead) => (
            <ListRow
              key={lead.id}
              title={getLeadName(lead)}
              subtitle={lead.user_query || lead.chat_summary || 'Hot lead has not been marked contacted.'}
              meta="Hot"
              tone="bg-red-100 text-red-700"
              to={path('/analytics?follow_up=uncontacted')}
            />
          )) : (
            <ListRow title="Hot leads not contacted" subtitle="Every hot lead has been contacted." meta={displayLabel('clear')} tone="bg-green-100 text-green-700" />
          )}
          <ListRow title="Conversations with no contact captured" subtitle="Review anonymous conversations and decide whether follow-up is needed." meta={canLoadConversationHistory ? data.conversationTotal : canOpenConversationHistory ? 'Growth plan' : 'Admins only'} tone="bg-blue-100 text-blue-700" to={canOpenConversationHistory ? path('/conversations') : undefined} />
          <ListRow title="Open tickets older than 2h / 24h" subtitle="Use support reminders and priority assignment for stale tickets." meta={openTickets} tone="bg-amber-100 text-amber-700" to={path('/support')} />
          <ListRow
            title="Who receives new lead alerts"
            subtitle={`Email: ${alertEmails.length ? plural(alertEmails.length, 'address', 'addresses') : 'your workspace admins'}. WhatsApp: ${
              alertPhones.length ? plural(alertPhones.length, 'number') : isGrowthOrScale ? 'no number yet' : 'on Growth and Scale plans'
            }.`}
            meta={displayLabel(whatsappGap ? 'check' : 'clear')}
            tone={whatsappGap ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}
            to={path('/notification-settings')}
          />
        </Section>

        <Section title="Agent performance" action={<Link className="text-sm text-indigo-600" to={path('/analytics/impact')}>Open impact</Link>}>
          <MetricLine label="Conversations (all time)" value={canLoadConversationHistory ? data.conversationTotal : 'Growth plan'} />
          <MetricLine label="Leads captured (last 30 days)" value={periodLeads} />
          <MetricLine label="Leads that became customers" value={leadConversionRate} />
          <MetricLine label="Support questions answered by AI alone" value={supportDeflection} />
          <MetricLine label="Top asked questions / intent themes" value="Open insights" to={path('/lead-intelligence')} />
        </Section>

        <Section title="Setup health">
          <HealthLine label="Agent live status" ok={agentHealth.label === 'Live'} detail={agentHealth.label} to={path('/agent-management')} />
          <HealthLine label="Knowledge Base pages" ok={knowledgePages > 0} detail={`${knowledgePages || 0} sources`} to={path('/rag/sources')} />
          <HealthLine
            label="Widget on your website"
            ok={widgetInstalled}
            detail={widgetInstalled
              ? `Seen on ${installHosts[0].host}, ${formatDate(installHosts[0].last_seen)}`
              : installKnown ? 'Not seen on a website yet' : 'Could not check'}
            to={path('/agent-management?install=first')}
          />
          <HealthLine label="WhatsApp handoff configured?" ok={tier === 'growth' || tier === 'scale'} detail="Check settings" to={path('/whatsapp-outbound')} />
          <HealthLine
            label="Lead alerts set up"
            ok={alertsKnown && !whatsappGap}
            detail={!alertsKnown
              ? 'Could not check'
              : whatsappGap ? 'Add a WhatsApp number so new leads reach your phone' : 'New leads reach your team'}
            to={path('/notification-settings')}
          />
          <HealthLine label="Support enabled?" ok={openTickets > 0 || data.supportMetrics?.total > 0} detail="Check support settings" to={path('/support-notifications')} />
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Recent leads" action={<Link className="text-sm text-indigo-600" to={path('/analytics')}>View all</Link>}>
          {data.leads.length ? data.leads.map((lead) => (
            <ListRow
              key={lead.id}
              title={getLeadName(lead)}
              subtitle={lead.user_query || lead.chat_summary || fmtDate(lead.created_at)}
              meta={displayLabel(lead.priority_tier || lead.status || 'lead')}
              tone={isHotLead(lead) ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}
              to={path('/analytics')}
            />
          )) : <EmptyState>No leads captured in the loaded window.</EmptyState>}
        </Section>

        <Section title="Quick actions">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <QuickAction icon={UserGroupIcon} label="View leads" to={path('/analytics')} />
            <QuickAction icon={ChatBubbleLeftRightIcon} label="Review conversations" to={path('/conversations')} disabled={!canOpenConversationHistory} />
            <QuickAction icon={LifebuoyIcon} label="Open support tickets" to={path('/support')} />
            <QuickAction icon={CircleStackIcon} label="Add knowledge source" to={path('/rag/sources')} />
            <QuickAction icon={MagnifyingGlassIcon} label="Install on your website" to={path('/agent-management?install=first')} />
            <QuickAction icon={BellAlertIcon} label="Configure notifications" to={path('/notification-settings')} />
          </div>
        </Section>
      </div>
    </div>
  );
};

const Header = ({ title, subtitle, refreshing, onRefresh }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{title}</h1>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
    </div>
    <button
      type="button"
      onClick={onRefresh}
      className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
    >
      <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
      Refresh
    </button>
  </div>
);

const Alert = ({ children }) => (
  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
    <div className="flex items-start gap-2">
      <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <span>{children}</span>
    </div>
  </div>
);

const MetricLine = ({ label, value, to }) => {
  const content = (
    <div className="flex items-center justify-between gap-3 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-sm font-semibold text-gray-900 dark:text-white">{value}</span>
    </div>
  );
  return to ? <Link to={to} className="block hover:bg-gray-50 dark:hover:bg-gray-900/30 -mx-2 px-2 rounded-md">{content}</Link> : content;
};

const HealthLine = ({ label, ok, detail, to }) => {
  const content = (
    <div className="flex items-center justify-between gap-3 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
      </div>
      <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${ok ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
        {ok ? 'OK' : 'Check'}
      </span>
    </div>
  );
  return to ? <Link to={to} className="block hover:bg-gray-50 dark:hover:bg-gray-900/30 -mx-2 px-2 rounded-md">{content}</Link> : content;
};

const QuickAction = ({ icon: Icon, label, to, disabled }) => {
  if (disabled) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-400 dark:text-gray-500">
        <Icon className="w-4 h-4" />
        {label}
      </div>
    );
  }
  return (
    <Link
      to={to}
      className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
    >
      <Icon className="w-4 h-4 text-indigo-600" />
      {label}
    </Link>
  );
};

export default WorkspaceDashboardPage;
